import { Check, X } from "lucide-react";
import { useCallback, useState } from "react";

import { useAcceptOrder } from "@/common/api";
import { Button, ConfirmModal, CommentDialog, LoadingModal } from "@/common/components";
import { useToast } from "@/common/hooks";

interface ModerationActionsProps {
  orderId: string;
  isModeration: boolean;
  onSuccess?: () => void;
}

export function ModerationActions({ orderId, isModeration, onSuccess }: ModerationActionsProps) {
  const { toast } = useToast();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    requestId: string;
    action: "approve";
  }>({
    isOpen: false,
    requestId: "",
    action: "approve",
  });

  const [rejectDialog, setRejectDialog] = useState<{
    isOpen: boolean;
    requestId: string;
  }>({
    isOpen: false,
    requestId: "",
  });

  const { mutateAsync: mutateAcceptOrderAsync, isPending: isAcceptOrderPending } = useAcceptOrder();

  const handleApprove = useCallback((requestId: string) => {
    setConfirmModal({
      isOpen: true,
      requestId,
      action: "approve",
    });
  }, []);

  const handleReject = useCallback((requestId: string) => {
    setRejectDialog({
      isOpen: true,
      requestId,
    });
  }, []);

  const handleConfirm = useCallback(() => {
    mutateAcceptOrderAsync({
      data: {
        id: confirmModal.requestId,
        isAccept: true,
      },
    })
      .then(() => {
        toast({
          title: "Заявка утверждена",
        });
        onSuccess?.();
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка модерации",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при модерации заявки",
        });
      });
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  }, [confirmModal, mutateAcceptOrderAsync, toast, onSuccess]);

  const handleRejectWithComment = useCallback(
    (requestId: string, comment: string) => {
      mutateAcceptOrderAsync({
        data: {
          id: requestId,
          isAccept: false,
          comment,
        },
      })
        .then(() => {
          toast({
            title: "Заявка отклонена",
          });
          setRejectDialog({ isOpen: false, requestId: "" });
          onSuccess?.();
        })
        .catch((error: unknown) => {
          toast({
            variant: "destructive",
            title: "Ошибка модерации",
            description:
              error instanceof Error ? error.message : "Произошла ошибка при модерации заявки",
          });
        });
    },
    [mutateAcceptOrderAsync, toast, onSuccess],
  );

  if (!isModeration) {
    return null;
  }

  return (
    <>
      <div className="flex gap-2 md:mb-0 mb-4">
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleApprove(orderId);
          }}
          disabled={isAcceptOrderPending}
          className="btn-primary"
          size="sm"
        >
          <Check className="h-4 w-4 mr-1" />
          <span>Утвердить</span>
        </Button>
        <Button
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            handleReject(orderId);
          }}
          disabled={isAcceptOrderPending}
          className="text-red-600 hover:text-white"
          size="sm"
        >
          <X className="h-4 w-4 mr-1" />
          <span>Отклонить</span>
        </Button>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Утверждение заявки"
        message="Вы уверены, что хотите утвердить эту заявку?"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        confirmText="Утвердить"
      />

      <CommentDialog
        isOpen={rejectDialog.isOpen}
        title="Отклонение заявки"
        description="Укажите причину отклонения заявки. Этот комментарий будет виден инициатору."
        isPending={isAcceptOrderPending}
        onConfirm={(comment) => {
          handleRejectWithComment(rejectDialog.requestId, comment);
        }}
        onCancel={() => setRejectDialog({ isOpen: false, requestId: "" })}
      />

      <LoadingModal isOpen={isAcceptOrderPending} message="Обработка заявки..." />
    </>
  );
}
